import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

function NotFoundPage() {
  const [isAnimated, setIsAnimated] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(0);
  const [reelFrame, setReelFrame] = useState(0);

  const errorMessages = [
    {
      title: "🎬 Scene Not Found!",
      subtitle: "This reel seems to be missing from the timeline...",
      description: "Don't worry! Even the best directors have deleted scenes. Let's get you back to creating viral content that captures attention."
    },
    {
      title: "📹 Camera Roll Empty!",
      subtitle: "No footage found at this location...",
      description: "Looks like this clip didn't make the final cut. Ready to generate some AI-powered faceless reels that go viral?"
    },
    {
      title: "🎞️ Film Break Detected!",
      subtitle: "The content reel has snapped at this frame...",
      description: "Technical difficulties happen to the best creators. Let's splice you back into your content creation workflow!"
    },
    {
      title: "🤖 AI Generator Offline!",
      subtitle: "This automation sequence doesn't exist...",
      description: "Our AI is taking a creative break. Time to redirect you to where the magic happens - your reel dashboard!"
    },
    {
      title: "📱 Content Buffer Underrun!",
      subtitle: "This viral moment hasn't been captured yet...",
      description: "Every creator's journey has unexpected cuts. Let's get you back to producing engaging faceless content!"
    }
  ];

  useEffect(() => {
    setIsAnimated(true);
    setCurrentMessage(Math.floor(Math.random() * errorMessages.length));
    
    // Animate film reel frames
    const interval = setInterval(() => {
      setReelFrame((prev) => (prev + 1) % 8);
    }, 200);
    
    return () => clearInterval(interval);
  }, []);

  const contentMetrics = [
    { icon: '👁️', label: 'Views', value: '404K' },
    { icon: '❤️', label: 'Likes', value: '404' },
    { icon: '💬', label: 'Comments', value: '0' },
    { icon: '🔄', label: 'Shares', value: '404' },
    { icon: '📈', label: 'Engagement', value: '404%' },
    { icon: '⏱️', label: 'Watch Time', value: '4:04' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center px-4 py-16 overflow-hidden relative">
      {/* Animated background with floating content icons */}
      <div className="absolute inset-0 opacity-10">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute text-4xl animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${15 + Math.random() * 10}s`
            }}
          >
            {['📱', '🎬', '📹', '🎥', '🎞️', '📺', '💫'][Math.floor(Math.random() * 7)]}
          </div>
        ))}
      </div>

      {/* Film reel animation */}
      <div className="absolute top-0 left-0 w-full h-32 bg-black/20 flex items-center overflow-hidden">
        <div className="flex space-x-4 animate-slide-left">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="w-24 h-20 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg opacity-50 flex items-center justify-center"
            >
              <div className={`w-16 h-16 rounded-full bg-black/30 ${i % 8 === reelFrame ? 'animate-pulse' : ''}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto text-center relative z-10">
        {/* Large 404 with reel theme */}
        <div className="relative mb-12">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-48 h-48 bg-purple-500 rounded-full animate-ping opacity-20" />
            <div className="w-36 h-36 bg-blue-500 rounded-full animate-ping opacity-30 animation-delay-500" />
          </div>
          
          <div className="relative">
            <div className="text-8xl sm:text-9xl font-black bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent tracking-tight mb-6">
              4
              <span className="inline-block mx-3 relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center animate-spin-slow">
                    <span className="text-white text-3xl sm:text-4xl">🎬</span>
                  </div>
                </div>
                0
              </span>
              4
            </div>
            
            {/* Content metrics bar */}
            <div className="flex justify-center flex-wrap gap-3 mb-8">
              {contentMetrics.map((metric, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 border border-purple-400/30 transform hover:scale-110 transition-all duration-300 animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-xl">{metric.icon}</span>
                    <div className="text-left">
                      <div className="text-xs text-gray-300">{metric.label}</div>
                      <div className="text-sm font-bold text-white">{metric.value}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Error Messages */}
        <div className="space-y-6 mb-12">
          <div className="space-y-2">
            <h1 className="text-3xl sm:text-4xl font-bold text-white animate-fade-in">
              {errorMessages[currentMessage].title}
            </h1>
            <p className="text-xl text-purple-200 animate-fade-in">
              {errorMessages[currentMessage].subtitle}
            </p>
          </div>

          <div className="bg-gradient-to-r from-purple-900/40 to-blue-900/40 backdrop-blur-sm rounded-2xl p-6 border border-purple-400/30 animate-fade-in">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
            </div>
            <p className="text-gray-200 text-lg leading-relaxed">
              {errorMessages[currentMessage].description}
            </p>
          </div>

          {/* Feature cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            <div className="bg-gradient-to-br from-purple-800/60 to-blue-800/60 backdrop-blur-sm rounded-xl p-4 border border-purple-400/30 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300 group">
              <div className="text-3xl mb-2 group-hover:animate-bounce">🤖</div>
              <h3 className="font-semibold text-white mb-1">AI Generator</h3>
              <p className="text-sm text-purple-200">Create faceless reels instantly</p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-800/60 to-blue-800/60 backdrop-blur-sm rounded-xl p-4 border border-purple-400/30 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300 group">
              <div className="text-3xl mb-2 group-hover:animate-bounce">📊</div>
              <h3 className="font-semibold text-white mb-1">Analytics</h3>
              <p className="text-sm text-purple-200">Track viral performance</p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-800/60 to-blue-800/60 backdrop-blur-sm rounded-xl p-4 border border-purple-400/30 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300 group">
              <div className="text-3xl mb-2 group-hover:animate-bounce">📅</div>
              <h3 className="font-semibold text-white mb-1">Scheduler</h3>
              <p className="text-sm text-purple-200">Automate your posting</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
          <Link
            to="/dashboard"
            className="group relative px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-bold text-lg hover:from-purple-700 hover:to-blue-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 rounded-xl blur opacity-30 group-hover:opacity-50 transition-opacity duration-300" />
            <span className="relative flex items-center space-x-2">
              <span>📊</span>
              <span>Back to Dashboard</span>
            </span>
          </Link>
          
          <Link
            to="/reels/create"
            className="px-8 py-4 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-xl font-bold text-lg hover:from-pink-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <span className="flex items-center space-x-2">
              <span>🎬</span>
              <span>Create New Reel</span>
            </span>
          </Link>
        </div>

        {/* Platform badges */}
        <div className="mt-12 flex justify-center space-x-4 opacity-60">
          <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-purple-400/30">
            <span className="text-white font-semibold">TikTok Ready</span>
          </div>
          <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-purple-400/30">
            <span className="text-white font-semibold">Instagram Reels</span>
          </div>
          <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-purple-400/30">
            <span className="text-white font-semibold">YouTube Shorts</span>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="mt-8 opacity-50">
          <div className="flex justify-center space-x-4">
            {['🎬', '📱', '🤖', '📈', '💫'].map((emoji, index) => (
              <span
                key={index}
                className="text-3xl animate-pulse"
                style={{
                  animationDelay: `${index * 0.3}s`,
                  animationDuration: '2s',
                }}
              >
                {emoji}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Film strip decoration at bottom */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-black/20 flex items-center overflow-hidden">
        <div className="flex space-x-4 animate-slide-right">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="w-24 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg opacity-50 flex items-center justify-center"
            >
              <div className={`w-16 h-16 rounded-full bg-black/30 ${(i + 4) % 8 === reelFrame ? 'animate-pulse' : ''}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default NotFoundPage;